/**
 * How the utilisation figure above is calculated — the operating hours, machine by machine,
 * with the reconciliation on the record.
 *
 * The chip is the point of the panel, not decoration: Σ the machine rows must equal the
 * served numerator (constitution rule 3), and a drill that does not reconcile to its
 * headline is a bug rather than a cosmetic issue. Group refuses honestly — Group utilisation
 * is a simple average of the site percentages, so no machine list sums to it.
 */

import React from 'react'
import { useAvailabilitySeries } from '../../hooks/useAvailability'
import { useUtilisation } from '../../hooks/useUtilisation'
import type { Site, UtilisationBreakdown } from '../../types/metrics'

const h1 = (v: number | null | undefined): string =>
  v == null ? '—' : `${v.toLocaleString('en-GB', { maximumFractionDigits: 1 })}h`

const STAT = 'font-mono text-[13px] font-semibold'

function Totals({ operating, available, pct }: {
  operating: number | null
  available: number | null
  pct: number | null
}): React.JSX.Element {
  const cells: [string, string, string][] = [
    ['Operating', h1(operating), 'var(--lc-ink)'],
    ['Resource available', h1(available), 'var(--lc-ink)'],
    ['Utilisation', pct == null ? '—' : `${pct.toFixed(1)}%`, 'var(--lc-blue)'],
  ]
  return (
    <div className="flex flex-wrap gap-[26px] pb-2.5">
      {cells.map(([k, v, color]) => (
        <div key={k} className="flex flex-col gap-px">
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[.08em]" style={{ color: 'var(--lc-mut)' }}>{k}</span>
          <span className={STAT} style={{ color }}>{v}</span>
        </div>
      ))}
    </div>
  )
}

function Machines({ data }: { data: UtilisationBreakdown }): React.JSX.Element {
  const key = data.level === 'machine' ? 'Machine' : 'WorkCentre'
  return (
    <div className="grid grid-cols-1 gap-x-[26px] lg:grid-cols-2">
      {data.rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_68px_46px] items-center gap-x-2.5 border-t py-1 font-mono text-[11px]"
          style={{ borderColor: 'var(--lc-b07)', color: 'var(--lc-ink2)' }}
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">{String(r[key])}</span>
          <span className="text-right tabular-nums">{h1(Number(r.OperatingHrs))}</span>
          <span className="text-right tabular-nums" style={{ color: 'var(--lc-mut)' }}>
            {r['Share%'] === null ? '—' : `${r['Share%']}%`}
          </span>
        </div>
      ))}
    </div>
  )
}

function Reconcile({ data }: { data: UtilisationBreakdown }): React.JSX.Element {
  const ok = data.sum_check.reconciles
  return (
    <span
      role={ok ? 'status' : 'alert'}
      className="whitespace-nowrap rounded-[5px] border px-2 py-[3px] font-mono text-[10.5px] font-medium"
      style={{
        color: ok ? 'var(--lc-teal-ink)' : 'var(--lc-red-ink)',
        background: ok ? 'var(--lc-teal-bg)' : 'var(--lc-red-bg)',
        borderColor: ok ? 'var(--lc-teal-line)' : 'var(--lc-red)',
      }}
    >
      {ok ? '✓ Reconciles' : '✕ Does not reconcile'} — Σ {h1(data.sum_check.rows_total)}{' '}
      {ok ? '=' : '≠'} served {h1(data.sum_check.cell_value)}
    </span>
  )
}

export default function UtilisationDisclosure({ site, from, to }: {
  site: Site
  from: string
  to: string
}): React.JSX.Element {
  const series = useAvailabilitySeries(site, from, to)
  const months = series.data?.months ?? []
  const month = months[months.length - 1]
  // The governed headline, from the series itself — see UtilisationCard.
  const basis = month?.bases.find((b) => b.basis === series.data?.headline_basis)
  const result = useUtilisation(site, month?.period ?? '')
  const breakdown = result.data && !('refused' in result.data) ? result.data : null
  return (
    <div
      className="mx-[22px] mt-3 rounded-[9px] border px-[15px] py-[13px]"
      style={{ borderColor: 'var(--lc-b09)', background: 'var(--lc-panel)' }}
    >
      <div className="flex items-baseline justify-between gap-4 pb-2.5">
        <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.09em]" style={{ color: 'var(--lc-mut)' }}>
          How utilisation is calculated · operating hours by {breakdown?.level === 'work_centre' ? 'work centre' : 'machine'}
          {month ? ` · ${month.period}` : ''}
        </span>
        {breakdown && <Reconcile data={breakdown} />}
      </div>
      <Totals operating={month?.operating_hrs ?? null} available={basis?.resource_hrs ?? null} pct={basis?.utilisation_pct ?? null} />
      {breakdown ? (
        <Machines data={breakdown} />
      ) : (
        <p className="font-sans text-[11.5px]" style={{ color: 'var(--lc-ink3)' }}>
          Group utilisation is a simple average of the site percentages — no machine list sums to it.
          Switch the scope to Kirkstall or Oracle to see the operating hours behind it.
        </p>
      )}
    </div>
  )
}
